import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Minesweeper.css';

const Minesweeper = () => {
  const [board, setBoard] = useState([]);
  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'lost'
  const [mineCount, setMineCount] = useState(10);
  const [flagCount, setFlagCount] = useState(0);
  const [firstClick, setFirstClick] = useState(true);
  const [difficulty, setDifficulty] = useState('beginner');

  // Game difficulty configurations - memoized to prevent recreation
  const difficulties = useMemo(() => ({
    beginner: { rows: 9, cols: 9, mines: 10 },
    intermediate: { rows: 16, cols: 16, mines: 40 },
    expert: { rows: 16, cols: 30, mines: 99 }
  }), []);

  const initializeBoard = useCallback((rows, cols) => {
    return Array(rows).fill().map(() =>
      Array(cols).fill().map(() => ({
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborCount: 0
      }))
    );
  }, []);

  const placeMines = useCallback((board, mines, firstRow, firstCol) => {
    const rows = board.length;
    const cols = board[0].length;
    let minesPlaced = 0;

    while (minesPlaced < mines) {
      const row = Math.floor(Math.random() * rows);
      const col = Math.floor(Math.random() * cols);

      // Don't place mine on first click or if already has mine
      if (!board[row][col].isMine && !(row === firstRow && col === firstCol)) {
        board[row][col].isMine = true;
        minesPlaced++;
      }
    }

    // Calculate neighbor counts
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (!board[row][col].isMine) {
          board[row][col].neighborCount = countNeighborMines(board, row, col);
        }
      }
    }
  }, []);

  const countNeighborMines = (board, row, col) => {
    let count = 0;
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        const newRow = row + i;
        const newCol = col + j;
        if (
          newRow >= 0 && newRow < board.length &&
          newCol >= 0 && newCol < board[0].length &&
          board[newRow][newCol].isMine
        ) {
          count++;
        }
      }
    }
    return count;
  };

  const revealCell = (row, col) => {
    if (gameState !== 'playing') return;

    const newBoard = board.map(r => r.map(c => ({ ...c })));
    
    if (firstClick) {
      placeMines(newBoard, mineCount, row, col);
      setFirstClick(false);
    }

    if (newBoard[row][col].isFlagged || newBoard[row][col].isRevealed) return;

    if (newBoard[row][col].isMine) {
      // Game over - reveal all mines
      for (let i = 0; i < newBoard.length; i++) {
        for (let j = 0; j < newBoard[0].length; j++) {
          if (newBoard[i][j].isMine) {
            newBoard[i][j].isRevealed = true;
          }
        }
      }
      setGameState('lost');
    } else {
      // Reveal cell and potentially neighbors
      revealCells(newBoard, row, col);
      
      // Check for win condition
      if (checkWin(newBoard)) {
        setGameState('won');
      }
    }

    setBoard(newBoard);
  };

  const revealCells = (board, row, col) => {
    if (
      row < 0 || row >= board.length ||
      col < 0 || col >= board[0].length ||
      board[row][col].isRevealed ||
      board[row][col].isFlagged ||
      board[row][col].isMine
    ) {
      return;
    }

    board[row][col].isRevealed = true;

    // If no neighboring mines, reveal all neighbors
    if (board[row][col].neighborCount === 0) {
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          revealCells(board, row + i, col + j);
        }
      }
    }
  };

  const toggleFlag = (e, row, col) => {
    e.preventDefault();
    if (gameState !== 'playing' || board[row][col].isRevealed) return;

    const newBoard = board.map(r => r.map(c => ({ ...c })));
    newBoard[row][col].isFlagged = !newBoard[row][col].isFlagged;
    
    setFlagCount(prev => newBoard[row][col].isFlagged ? prev + 1 : prev - 1);
    setBoard(newBoard);
  };

  const checkWin = (board) => {
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[0].length; col++) {
        const cell = board[row][col];
        if (!cell.isMine && !cell.isRevealed) {
          return false;
        }
      }
    }
    return true;
  };

  const newGame = useCallback((newDifficulty = difficulty) => {
    const config = difficulties[newDifficulty];
    const newBoard = initializeBoard(config.rows, config.cols);
    setBoard(newBoard);
    setGameState('playing');
    setMineCount(config.mines);
    setFlagCount(0);
    setFirstClick(true);
    setDifficulty(newDifficulty);
  }, [difficulty, difficulties, initializeBoard]);

  useEffect(() => {
    newGame();
  }, [newGame]);

  const getCellContent = (cell) => {
    if (cell.isFlagged) return '🚩';
    if (!cell.isRevealed) return '';
    if (cell.isMine) return '💣';
    if (cell.neighborCount === 0) return '';
    return cell.neighborCount;
  };

  const getCellClass = (cell) => {
    let className = 'minesweeper-cell';
    if (cell.isRevealed) {
      className += ' revealed';
      if (cell.isMine) {
        className += ' mine';
      } else if (cell.neighborCount > 0) {
        className += ` number-${cell.neighborCount}`;
      }
    } else {
      className += ' hidden';
    }
    if (cell.isFlagged) className += ' flagged';
    return className;
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">💣 Minesweeper</h1>
        <div className="game-instructions">
          Left click to reveal cells, right click to flag mines. Find all mines using number clues!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {gameState === 'playing' ? `Mines: ${mineCount - flagCount} 😐` : 
           gameState === 'won' ? 'You Won! 😎' : 'Game Over! 😵'}
        </div>

        <div className="difficulty-selector">
          {Object.keys(difficulties).map(diff => (
            <button
              key={diff}
              onClick={() => newGame(diff)}
              className={`btn ${difficulty === diff ? 'btn-primary' : 'btn-secondary'}`}
            >
              {diff.charAt(0).toUpperCase() + diff.slice(1)}
            </button>
          ))}
        </div>

        <div className="game-play-area">
          <div 
            className="minesweeper-board"
            style={{
              gridTemplateColumns: `repeat(${board[0]?.length || 9}, 1fr)`,
              maxWidth: `${(board[0]?.length || 9) * 30 + 20}px`
            }}
          >
            {board.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={getCellClass(cell)}
                  onClick={() => revealCell(rowIndex, colIndex)}
                  onContextMenu={(e) => toggleFlag(e, rowIndex, colIndex)}
                >
                  {getCellContent(cell)}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={() => newGame()}>
          Reset Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default Minesweeper;